export type ImageTransformOptions = {
  width?: number;
  quality?: number; // 1-100
  format?: 'webp' | 'avif' | 'auto';
};

/**
 * 返回按环境切换的图片 URL。
 * - 开发环境：返回原始 public 路径，例如 /images/xxx.jpg
 * - 生产环境：返回 Cloudflare Resize 路径，例如 /cdn-cgi/image/width=...,format=webp,quality=70/images/xxx.jpg
 */
export function getImageUrl(path: string, opts: ImageTransformOptions = {}): string {
  const { width, quality = 70, format = 'webp' } = opts;

  if (!path) return path;
  // 绝对 URL 直接返回
  if (/^https?:\/\//i.test(path)) return path;

  // 确保以 / 开头（public 路径）
  const normalized = path.startsWith('/') ? path : `/${path}`;

  // 仅当显式开启 REACT_APP_USE_CF_IMAGE 且在生产并非本地/离线时启用 Cloudflare Resize
  const isBrowser = typeof window !== 'undefined';
  const hostname = isBrowser ? window.location.hostname : '';
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local');
  const isFileProtocol = isBrowser ? window.location.protocol === 'file:' : false;
  const useCloudflare = process.env.REACT_APP_USE_CF_IMAGE === 'true' && process.env.NODE_ENV === 'production' && !isLocalHost && !isFileProtocol;

  if (!useCloudflare) {
    return normalized; // 默认返回原始路径，保证可见性
  }

  const params: string[] = [];
  if (width && Number.isFinite(width)) params.push(`width=${Math.round(width)}`);
  if (format) params.push(`format=${format}`);
  if (quality && Number.isFinite(quality)) params.push(`quality=${Math.round(quality)}`);
  const prefix = params.length ? `/cdn-cgi/image/${params.join(',')}` : '/cdn-cgi/image';
  return `${prefix}${normalized}`;
}

/**
 * 返回按环境切换的 srcset 文本。
 * - 开发环境：返回空串（避免构建和本地 404）
 * - 生产环境：返回带不同 width 的 /cdn-cgi/image 路径集合
 */
export function getSrcSet(path: string, widths: number[], baseOpts: Omit<ImageTransformOptions, 'width'> = {}): string {
  if (process.env.NODE_ENV !== 'production') return '';
  const uniqueWidths = Array.from(new Set(widths.filter(w => Number.isFinite(w) && w > 0).map(w => Math.round(w))));
  return uniqueWidths
    .map(w => `${getImageUrl(path, { ...baseOpts, width: w })} ${w}w`)
    .join(', ');
}


